import type { ItemId } from "./items.js";
import type { SkillId } from "./skills.js";

export interface RecipeDef {
  id: string;
  name: string;
  output: ItemId;
  outputQty: number;
  inputs: { itemId: ItemId; quantity: number }[];
  reqLevel: number;
  xp: number;
  /** Authoritative channel time in milliseconds. */
  durationMs: number;
  category: "treat" | "tool" | "misc";
}

/** Tool recipes → Toycraft; treat/misc → Treatcraft. */
export function recipeSkillId(recipe: RecipeDef): SkillId {
  return recipe.category === "tool" ? "toycraft" : "treatcraft";
}

export const RECIPES: RecipeDef[] = [
  {
    id: "bronze_treat",
    name: "Bronze Treat",
    output: "bronze_treat",
    outputQty: 1,
    inputs: [
      { itemId: "pebble", quantity: 1 },
      { itemId: "bone_shard", quantity: 1 },
    ],
    reqLevel: 1,
    xp: 42,
    durationMs: 1500,
    category: "treat",
  },
  {
    id: "iron_treat",
    name: "Iron Treat",
    output: "iron_treat",
    outputQty: 1,
    inputs: [{ itemId: "iron_nugget", quantity: 1 }],
    reqLevel: 10,
    xp: 80,
    durationMs: 2500,
    category: "treat",
  },
  {
    id: "steel_treat",
    name: "Steel Treat",
    output: "steel_treat",
    outputQty: 1,
    inputs: [
      { itemId: "iron_nugget", quantity: 1 },
      { itemId: "charcoal", quantity: 2 },
    ],
    reqLevel: 15,
    xp: 120,
    durationMs: 2500,
    category: "treat",
  },
  {
    id: "silver_treat",
    name: "Silver Treat",
    output: "silver_treat",
    outputQty: 1,
    inputs: [
      { itemId: "silver_tag", quantity: 1 },
      { itemId: "charcoal", quantity: 2 },
    ],
    reqLevel: 20,
    xp: 180,
    durationMs: 4000,
    category: "treat",
  },
  {
    id: "gold_treat",
    name: "Gold Treat",
    output: "gold_treat",
    outputQty: 1,
    inputs: [
      { itemId: "gold_medallion", quantity: 1 },
      { itemId: "charcoal", quantity: 3 },
    ],
    reqLevel: 40,
    xp: 280,
    durationMs: 5000,
    category: "treat",
  },
  {
    id: "nova_treat",
    name: "Nova Treat",
    output: "nova_treat",
    outputQty: 1,
    inputs: [
      { itemId: "nova_bone", quantity: 1 },
      { itemId: "charcoal", quantity: 4 },
    ],
    reqLevel: 70,
    xp: 500,
    durationMs: 8000,
    category: "treat",
  },
  {
    id: "iron_shovel",
    name: "Iron Shovel",
    output: "iron_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "bronze_treat", quantity: 2 },
      { itemId: "stick", quantity: 1 },
    ],
    reqLevel: 1,
    xp: 80,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "steel_shovel",
    name: "Steel Shovel",
    output: "steel_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "iron_treat", quantity: 2 },
      { itemId: "stick", quantity: 1 },
    ],
    reqLevel: 10,
    xp: 140,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "silver_shovel",
    name: "Silver Shovel",
    output: "silver_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "steel_treat", quantity: 2 },
      { itemId: "hardwood_stick", quantity: 1 },
    ],
    reqLevel: 15,
    xp: 200,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "gold_shovel",
    name: "Gold Shovel",
    output: "gold_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "silver_treat", quantity: 2 },
      { itemId: "maple_branch", quantity: 1 },
    ],
    reqLevel: 20,
    xp: 280,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "diamond_shovel",
    name: "Diamond Shovel",
    output: "diamond_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "gold_treat", quantity: 2 },
      { itemId: "enchanted_stick", quantity: 1 },
    ],
    reqLevel: 40,
    xp: 400,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "nova_shovel",
    name: "Nova Shovel",
    output: "nova_shovel",
    outputQty: 1,
    inputs: [
      { itemId: "nova_treat", quantity: 2 },
      { itemId: "enchanted_stick", quantity: 1 },
    ],
    reqLevel: 70,
    xp: 600,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "iron_ball",
    name: "Iron Ball",
    output: "iron_ball",
    outputQty: 1,
    inputs: [
      { itemId: "bronze_treat", quantity: 2 },
      { itemId: "twig", quantity: 1 },
    ],
    reqLevel: 1,
    xp: 80,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "steel_ball",
    name: "Steel Ball",
    output: "steel_ball",
    outputQty: 1,
    inputs: [
      { itemId: "iron_treat", quantity: 2 },
      { itemId: "stick", quantity: 1 },
    ],
    reqLevel: 10,
    xp: 140,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "silver_ball",
    name: "Silver Ball",
    output: "silver_ball",
    outputQty: 1,
    inputs: [
      { itemId: "steel_treat", quantity: 2 },
      { itemId: "hardwood_stick", quantity: 1 },
    ],
    reqLevel: 15,
    xp: 200,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "gold_ball",
    name: "Gold Ball",
    output: "gold_ball",
    outputQty: 1,
    inputs: [
      { itemId: "silver_treat", quantity: 2 },
      { itemId: "maple_branch", quantity: 1 },
    ],
    reqLevel: 20,
    xp: 280,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "diamond_ball",
    name: "Diamond Ball",
    output: "diamond_ball",
    outputQty: 1,
    inputs: [
      { itemId: "gold_treat", quantity: 2 },
      { itemId: "enchanted_stick", quantity: 1 },
    ],
    reqLevel: 40,
    xp: 400,
    durationMs: 2000,
    category: "tool",
  },
  {
    id: "nova_ball",
    name: "Nova Ball",
    output: "nova_ball",
    outputQty: 1,
    inputs: [
      { itemId: "nova_treat", quantity: 2 },
      { itemId: "enchanted_stick", quantity: 1 },
    ],
    reqLevel: 70,
    xp: 600,
    durationMs: 2000,
    category: "tool",
  },
];

export const RECIPE_BY_ID: Record<string, RecipeDef> = Object.fromEntries(
  RECIPES.map((recipe) => [recipe.id, recipe]),
);

export function recipeById(id: string): RecipeDef | undefined {
  return RECIPE_BY_ID[id];
}
